import type { GlassVariant, Product } from "../types";
// De PUBLIEKE tweeling (netto-prijzen, korting 0, baked douche-prijs) — gegenereerd door
// scripts/build-glass-manual-public.mjs uit de volle glass-manual.json. De volle versie
// (bruto + dealerkorting) mag NOOIT hier geïmporteerd worden: alles wat deze module raakt,
// belandt in de client-bundel. Zie ook supplierInfo (functions) voor de kortingen zelf.
import manual from "../data/glass-manual.public.json";

/**
 * Handmatige glassoorten uit de Timmers-PRIJSLIJST (PDF), die níet in de Timmers-API (`glass.json`)
 * zitten — bv. Gehard glas (ESG). Ze worden als extra subcategorieën in het bestaande `enkel-glas`-
 * product gemerged, zodat ze zich exact als Floatglas/Figuurglas gedragen (configurator, overzichten,
 * overrides). De inkoopprijs = bruto × (1 − korting%); ops/vormen/grootte-toeslag erven van een
 * template-variant met dezelfde dikte (bewerkingsprijs hangt alleen van dikte af — zie memory).
 */

interface ManualKleur {
  color: string;
  /** In de publieke json zijn dit al NETTO-bedragen (de generator paste de korting toe). */
  bruto: Record<string, number>;
  /** Alleen douche-soorten: dikte/samenstelling → m²-prijs in CENTEN, hernormaliseerd naar de
   *  douche-dealerkorting. Vooraf uitgerekend zodat de korting zelf niet mee hoeft. */
  showerPerM2?: Record<string, number>;
}
interface ManualSoort {
  subcategory: string;
  productName: string;
  discountPct: number;
  minAreaM2: number;
  /** Doelproduct waar deze soort in gemerged wordt (default = `_templateProductSlug`,
   *  bv. "enkel-glas"). Gekleurd-glas-soorten gebruiken "gekleurd-glas" → die vervangen
   *  de (door de Timmers-feed gehusselde) varianten van dat product. */
  targetProductSlug?: string;
  /**
   * Hoe deze soort zich tot het doelproduct verhoudt.
   * - standaard (geen waarde): de varianten VERVANGEN die van het doelproduct;
   * - `"aanvullen"`: de varianten van het doelproduct blijven staan en zijn LEIDEND; deze soort
   *   vult alleen aan wat daar ontbreekt. Voor gehard glas, dat Timmers sinds 03-10-2026 ook via
   *   de API levert maar lang niet compleet (helder bestaat daar alleen in 4 en 5 mm).
   */
  mergeMode?: "aanvullen";
  /**
   * Kleurnaam uit onze prijslijst → de naam die de API voor diezelfde uitvoering gebruikt
   * ("Helder" → "Gehard blank"). Alleen zinvol samen met `mergeMode: "aanvullen"`: zonder deze
   * omzetting staan dezelfde ruiten als twee aparte kleuren in één rubriek.
   */
  colorAlias?: Record<string, string>;
  kleuren: ManualKleur[];
  /** Max. zijdeverhouding lang:kort (hardglas = 10, i.v.m. vervorming in de hardingsoven). */
  maxAspectRatio?: number;
  /** Minimum-maat per zijde (mm); overschrijft de algemene standaard. */
  minSideMm?: number;
  /** Minimum voor de langste zijde (mm) — bv. geëmailleerd 200 × 300. */
  minLongSideMm?: number;
  /** Minimum-diagonaal (mm) — bv. Matelac 250 mm diagonaal. */
  minDiagonalMm?: number;
  /** Simpele-mm soort (gehard): plaatmaat per dikte. */
  plateByThickness?: Record<string, [number, number]>;
  /** Simpele-mm soort met een gelaagde opbouw (beloopbaar): mm-sleutel → samenstellingscode
   *  (bv. "25" → "688.8") die als dikte-label + samenstelling-uitleg getoond wordt. */
  compositionByThickness?: Record<string, string>;
  /** "composition" = gelaagd: bruto-sleutels zijn samenstellingscodes (33.1) i.p.v. mm. */
  mode?: "composition";
  /** Composition: code → effectieve mm (gewicht/sortering/bewerkingsstaffel). */
  compositions?: Record<string, number>;
  /** Composition: effectieve mm → Platpoly slijp €/m ×100 (bruto). */
  slijpBrutoByMm?: Record<string, number>;
  /** Composition: korting% op het slijpwerk (operations). */
  opsDiscountPct?: number;
  /** Composition: één plaatmaat [kort, lang] voor de hele soort. */
  plate?: [number, number];
}
interface ManualData {
  _templateProductSlug: string;
  _templateSubcategory: string;
  _templateColor: string;
  /** false = prijzen blijven BRUTO (prijslijst) zodat ze tegen de PDF te checken zijn; korting niet toegepast. */
  _applyDiscount?: boolean;
  /** Doelproducten die NIET in de basiscatalogus bestaan → als eigen product aanmaken
   *  (i.p.v. mergen in een bestaand product). Bv. "installatieglas" met eigen URL/soort. */
  _newProducts?: Record<string, { name: string; category: string; description?: string }>;
  soorten: ManualSoort[];
}

const data = manual as unknown as ManualData;

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Template-variant met de gevraagde dikte (of de dichtstbijzijnde) voor ops/vormen/gewicht. */
function pickTemplate(templates: Map<number, GlassVariant>, mm: number): GlassVariant | undefined {
  if (templates.has(mm)) return templates.get(mm);
  let best: GlassVariant | undefined;
  let bestDiff = Infinity;
  for (const [t, v] of templates) {
    const d = Math.abs(t - mm);
    if (d < bestDiff) { bestDiff = d; best = v; }
  }
  return best;
}

/**
 * Geeft een nieuwe productenlijst terug met de handmatige glassoorten erbij gemerged in het
 * template-product (`enkel-glas`). Laat de overige producten ongemoeid.
 */
/** Sleutel waarop een API-variant en een prijslijst-variant dezelfde ruit beschrijven. */
const variantSleutel = (v: { subcategory?: string; color?: string; thicknessMm: number }): string =>
  `${v.subcategory ?? ""}|${v.color ?? ""}|${v.thicknessMm}`;

/**
 * API-varianten blijven staan; de prijslijst vult aan wat ontbreekt.
 *
 * Eén detail dat geld kost als je het vergeet: de douche-configurator rekent met
 * `showerPricePerM2` — glas dat in een douche gaat valt onder een andere dealerkorting (55% in
 * plaats van 57%) en is dus iets DUURDER dan hetzelfde glas in de glasconfigurator. Dat veld zit
 * alleen op onze prijslijst-varianten. Laat je de API-variant kaal winnen, dan rekent de douche
 * stilletjes met de goedkopere prijs. Daarom erft de API-variant dat veld van zijn tweelingregel.
 * (De gewone m²-prijzen zijn in alle 40 overlappende combinaties identiek, dus daar verandert
 * niets.) (12-10-2026.)
 */
function aanvullen(p: Product, bucket: { variants: GlassVariant[]; subs: string[] }): Product {
  const basis = p.variants ?? [];
  const perSleutel = new Map(basis.map((v) => [variantSleutel(v), v]));
  const extra: GlassVariant[] = [];
  const verrijkt = new Map<string, GlassVariant>();
  for (const v of bucket.variants) {
    const sleutel = variantSleutel(v);
    const bestaand = perSleutel.get(sleutel);
    if (!bestaand) { extra.push(v); continue; }
    if (v.showerPricePerM2 != null && bestaand.showerPricePerM2 == null) {
      verrijkt.set(sleutel, { ...bestaand, showerPricePerM2: v.showerPricePerM2 });
    }
  }
  const variants = basis.map((v) => verrijkt.get(variantSleutel(v)) ?? v).concat(extra);
  // De rubriek blijft die van de API; een aangevulde soort voegt er geen nieuwe aan toe.
  const subs = [...new Set([...(p.subcategories ?? []), ...bucket.subs])];
  return { ...p, variants, subcategories: subs };
}

export function mergeManualGlass(base: Product[]): Product[] {
  if (!data.soorten?.length) return base;

  // Template-varianten per dikte (Floatglas blank) uit het template-product.
  const host = base.find((p) => p.slug === data._templateProductSlug);
  if (!host) return base;
  const templates = new Map<number, GlassVariant>();
  for (const v of host.variants ?? []) {
    if (v.subcategory === data._templateSubcategory && v.color === data._templateColor) {
      templates.set(v.thicknessMm, v);
    }
  }
  if (templates.size === 0) return base;

  // Per doelproduct (enkel-glas / gekleurd-glas) de extra varianten + subcategorieën.
  const byTarget = new Map<string, { variants: GlassVariant[]; subs: string[]; aanvullen?: boolean }>();
  const bucketFor = (slug: string) => {
    let b = byTarget.get(slug);
    if (!b) { b = { variants: [], subs: [] }; byTarget.set(slug, b); }
    return b;
  };
  // Korting alleen toepassen als de schakelaar aan staat; anders blijven prijzen BRUTO (checkbaar).
  const applyDiscount = data._applyDiscount === true;
  for (const soort of data.soorten) {
    const bucket = bucketFor(soort.targetProductSlug ?? data._templateProductSlug);
    bucket.subs.push(soort.subcategory);
    if (soort.mergeMode === "aanvullen") bucket.aanvullen = true;
    const factor = applyDiscount ? 1 - soort.discountPct / 100 : 1;
    const opsFactor = applyDiscount ? 1 - (soort.opsDiscountPct ?? 0) / 100 : 1;
    for (const kleur of soort.kleuren) {
      for (const [key, bruto] of Object.entries(kleur.bruto)) {
        if (!(bruto > 0)) continue;
        const isComp = soort.mode === "composition";
        // Effectieve mm (gewicht/template/slijpstaffel) + een UNIEKe thicknessMm: meerdere
        // samenstellingen kunnen dezelfde effectieve mm hebben (33.1 & 33.2 = 6mm) maar moeten
        // aparte dikte-keuzes blijven → effMm + folie-aantal/100 (6.01, 6.02).
        const effMm = isComp ? soort.compositions?.[key] : Number(key);
        if (effMm == null || !Number.isFinite(effMm)) continue;
        const foil = isComp ? Number(key.split(".")[1] ?? 0) : 0;
        const uniqueMm = isComp ? Math.round((effMm + foil / 100) * 100) / 100 : effMm;
        const tmpl = pickTemplate(templates, effMm);
        if (!tmpl) continue;

        // Beloopbaar: prijs + naam + dikte-chip blijven de tótale mm ("25 mm"); de samenstellingscode
        //   (bv. "688.8") gaat als thicknessComposition mee zodat we de opbouw (ruiten + folies) er
        //   los ónder de chip kunnen uitleggen — net als de doorsnede bij gelaagd/isolatieglas.
        const compCode = !isComp ? soort.compositionByThickness?.[key] : undefined;
        const label = isComp ? key : `${effMm} mm`;
        const plate = isComp ? soort.plate : soort.plateByThickness?.[key];

        // Gelaagd/gehard-gelaagd heeft een eigen Platpoly-slijpstaffel (andere prijs dan
        // float) voor de RAND, maar boring/uitsparing/hoek mogen óók (prijs hangt alleen
        // van de dikte af) → behoud de template-ops en overschrijf alleen de randafwerking.
        let ops = tmpl.ops;
        if (isComp && soort.slijpBrutoByMm) {
          const slijp = Math.round((soort.slijpBrutoByMm[String(effMm)] ?? 0) * opsFactor);
          if (slijp > 0) {
            ops = { ...tmpl.ops, slijpPerM: slijp, edgeFinishes: [{ id: "polijst", label: "Gepolijst (Platpoly)", perM: slijp }] };
          }
        }

        // Levert de API dezelfde soort ook, dan hanteert die eigen kleurnamen ("Gehard blank"
        // waar onze prijslijst "Helder" zegt). Zonder omzetting staan ze als twee losse kleuren
        // naast elkaar in dezelfde rubriek. Zie `colorAlias` in glass-manual.json.
        const kleurNaam = soort.colorAlias?.[kleur.color] ?? kleur.color;
        bucket.variants.push({
          ...tmpl,
          id: slugify(`${soort.subcategory} ${kleurNaam} ${key}`),
          name: `${soort.subcategory} ${kleurNaam} ${label}`,
          finish: `${soort.subcategory} ${kleurNaam}`,
          color: kleurNaam,
          subcategory: soort.subcategory,
          thicknessMm: uniqueMm,
          ...(isComp ? { thicknessLabel: key } : {}),
          ...(compCode ? { thicknessComposition: compCode } : {}),
          pricePerM2: Math.round(bruto * factor * 100),
          minAreaM2: soort.minAreaM2,
          maxWidthMm: plate ? plate[0] : tmpl.maxWidthMm,
          maxHeightMm: plate ? plate[1] : tmpl.maxHeightMm,
          ...(soort.maxAspectRatio != null ? { maxAspectRatio: soort.maxAspectRatio } : {}),
          ...(soort.minSideMm != null ? { minSideMm: soort.minSideMm } : {}),
          ...(soort.minLongSideMm != null ? { minLongSideMm: soort.minLongSideMm } : {}),
          ...(soort.minDiagonalMm != null ? { minDiagonalMm: soort.minDiagonalMm } : {}),
          weightPerM2: effMm * 2.5,
          ops,
          // De korting zelf reist niet meer mee (publieke json heeft 'm op 0); de douche-engine
          // gebruikt de vooraf uitgerekende showerPricePerM2, de bestelbon de supplierDiscounts-
          // callable. Alleen een echt percentage (>0, volle json op de server) gaat nog mee.
          ...(soort.discountPct > 0 ? { timmersDiscountPct: soort.discountPct } : {}),
          ...(kleur.showerPerM2?.[key] != null ? { showerPricePerM2: kleur.showerPerM2[key] } : {}),
          imageUrl: undefined,
        });
      }
    }
  }

  const merged = base.map((p) => {
    const bucket = byTarget.get(p.slug);
    if (!bucket) return p;
    if (p.slug === data._templateProductSlug) {
      // Template-product (enkel-glas): de handmatige soorten ERBIJ (float/figuur blijven).
      return { ...p, variants: [...(p.variants ?? []), ...bucket.variants], subcategories: [...(p.subcategories ?? []), ...bucket.subs] };
    }
    // Aanvullen (gehard glas): de API is leidend en blijft staan; de prijslijst vult alleen de
    // combinaties aan die daar ontbreken. Zonder dit stonden beide bronnen als twee aparte
    // rubrieken op de site, met dezelfde naam en elk een eigen (klopende) prijs.
    if (bucket.aanvullen) return aanvullen(p, bucket);
    // Ander doelproduct (gekleurd-glas): de gehusselde Timmers-varianten VERVANGEN door
    // de prijslijst-soorten (Lacobel-Safe/T, Matelac-Safe/T, Geëmailleerd, Gehard gelakt).
    return { ...p, variants: bucket.variants, subcategories: bucket.subs };
  });

  // Doelproducten die niet in de basiscatalogus bestaan → als eigen product aanmaken
  // (eigen slug/URL/soort). Vorm (type/images) erft van het template-product; alléén de
  // variants/subcategorieën zijn nieuw. Zo krijgt bv. installatieglas exact dezelfde
  // enkel-glas-configurator op /glas-op-maat/<slug>.
  const created: Product[] = [];
  for (const [slug, bucket] of byTarget) {
    if (base.some((p) => p.slug === slug)) continue;
    const meta = data._newProducts?.[slug];
    if (!meta) continue;
    created.push({
      ...host,
      id: slug,
      slug,
      name: meta.name,
      category: meta.category,
      description: meta.description ?? "",
      subcategories: bucket.subs,
      variants: bucket.variants,
    });
  }

  return created.length ? [...merged, ...created] : merged;
}
